import { Link } from "@tanstack/solid-router";
import { IconTriangleAlert } from "~/components/icons";
import { buttonVariants } from "~/components/ui/button";
import { cn } from "~/lib/utils";

/**
 * A polished "page not found" state used as the router-wide
 * `defaultNotFoundComponent` and the dashboard layout's `notFoundComponent`.
 *
 * Renders a friendly 404 with a way back to a known-good location instead of
 * TanStack Router's bare `<p>Not Found</p>` fallback (which left the app
 * looking hung when an AI agent linked to a route that doesn't exist).
 */
export function NotFound({
  title = "Page not found",
  message = "The page you're looking for doesn't exist or may have been moved.",
  homeTo = "/",
  homeLabel = "Go to dashboard",
}: {
  title?: string;
  message?: string;
  homeTo?: string;
  homeLabel?: string;
}) {
  return (
    <div class="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <div class="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
        <IconTriangleAlert class="h-7 w-7 text-muted-foreground" />
      </div>
      <div class="space-y-1">
        <h1 class="text-3xl font-bold tracking-tight">{title}</h1>
        <p class="max-w-md text-muted-foreground">{message}</p>
      </div>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class={cn(buttonVariants({ variant: "outline" }))}
          onClick={() => window.history.back()}
        >
          Go back
        </button>
        <Link to={homeTo} class={cn(buttonVariants())}>
          {homeLabel}
        </Link>
      </div>
    </div>
  );
}
